import api from './api';

// Public order endpoints
const createPublicOrder = async (orderData) => {
  try {
    // The merchant is automatically determined from the current logged-in user
    const response = await api.post('/orders/public/create/', orderData);
    return response.data;
  } catch (error) {
    console.error('Error creating public order:', error);
    throw error;
  }
};

const getPublicOrderStatus = async (orderId) => {
  try {
    const response = await api.get(`/orders/public/${orderId}/status/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching public order status for order ${orderId}:`, error);
    throw error;
  }
};

const getPublicOrderByNumber = async (orderNumber) => {
  try {
    const response = await api.get(`/orders/public/track/${orderNumber}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching public order by number ${orderNumber}:`, error);
    throw error;
  }
};

const trackOrder = async (orderNumber) => {
  try {
    const response = await api.get(`/orders/public/track/${orderNumber}/`);
    return response.data;
  } catch (error) {
    console.error(`Error tracking order ${orderNumber}:`, error);
    throw error;
  }
};

const getCustomerOrders = async (merchantId, tableId) => {
  try {
    const params = {
      merchant: merchantId,
      table: tableId
    };

    const response = await api.get('/orders/public/customer-orders/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching customer orders:', error);
    throw error;
  }
};

// Admin order endpoints
const getOrders = async (params = {}) => {
  try {
    // The merchant is automatically determined from the current logged-in user
    const response = await api.get('/orders/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

const getOrder = async (id) => {
  try {
    const response = await api.get(`/orders/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching order ${id}:`, error);
    throw error;
  }
};

const updateOrder = async (id, data) => {
  try {
    const response = await api.patch(`/orders/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating order ${id}:`, error);
    throw error;
  }
};

const updateOrderStatus = async (id, status, notes = '') => {
  try {
    const response = await api.patch(`/orders/${id}/update-status/`, {
      status,
      notes
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating order ${id} status:`, error);
    throw error;
  }
};

const cancelOrder = async (id, reason = '') => {
  try {
    const response = await api.post(`/orders/${id}/cancel/`, {
      reason
    });
    return response.data;
  } catch (error) {
    console.error(`Error cancelling order ${id}:`, error);
    throw error;
  }
};

const getOrderItems = async (params = {}) => {
  try {
    // The merchant is automatically determined from the current logged-in user
    const response = await api.get('/orders/items/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching order items:', error);
    throw error;
  }
};

const getOrderItem = async (id) => {
  try {
    const response = await api.get(`/orders/items/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching order item ${id}:`, error);
    throw error;
  }
};

const updateOrderItem = async (id, data) => {
  try {
    const response = await api.patch(`/orders/items/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating order item ${id}:`, error);
    throw error;
  }
};

const createOrderItem = async (data) => {
  try {
    const response = await api.post('/orders/items/', data);
    return response.data;
  } catch (error) {
    console.error('Error creating order item:', error);
    throw error;
  }
};

const deleteOrderItem = async (id) => {
  try {
    await api.delete(`/orders/items/${id}/`);
  } catch (error) {
    console.error(`Error deleting order item ${id}:`, error);
    throw error;
  }
};

const getOrderStatusHistory = async (orderId) => {
  try {
    const response = await api.get('/orders/status-history/', {
      params: { order: orderId }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching order status history for order ${orderId}:`, error);
    throw error;
  }
};

const getOrderStatistics = async (params = {}) => {
  try {
    // The merchant is automatically determined from the current logged-in user
    const response = await api.get('/orders/statistics/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching order statistics:', error);
    throw error;
  }
};

const getOrdersByStatus = async (params = {}) => {
  try {
    // The merchant is automatically determined from the current logged-in user
    const response = await api.get('/orders/by-status/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching orders by status:', error);
    throw error;
  }
};

const getOrdersByStatusPaginated = async (status, page = 1, pageSize = 10) => {
  try {
    const response = await api.get('/orders/by-status/', {
      params: {
        status,
        page,
        page_size: pageSize
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching paginated orders for status ${status}:`, error);
    throw error;
  }
};

const getOrdersByDate = async (params = {}) => {
  try {
    // The merchant is automatically determined from the current logged-in user
    const response = await api.get('/orders/by-date/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching orders by date:', error);
    throw error;
  }
};

const getPopularItems = async (params = {}) => {
  try {
    const response = await api.get('/orders/popular-items/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching popular items:', error);
    throw error;
  }
};

const getPeakHours = async (params = {}) => {
  try {
    const response = await api.get('/orders/peak-hours/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching peak hours:', error);
    throw error;
  }
};

const getStatusBreakdown = async (params = {}) => {
  try {
    const response = await api.get('/orders/status-breakdown/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching status breakdown:', error);
    throw error;
  }
};

const markOrderAsPaid = async (orderId, discountIds = []) => {
  try {
    const payload = discountIds.length > 0 ? { discount_ids: discountIds } : {};
    const response = await api.post(`/orders/${orderId}/mark-paid/`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error marking order ${orderId} as paid:`, error);
    throw error;
  }
};

const getAvailableDiscounts = async (orderId) => {
  try {
    const response = await api.get(`/orders/${orderId}/available-discounts/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching available discounts for order ${orderId}:`, error);
    throw error;
  }
};

// FSM Transition functions
const getAvailableTransitions = async (orderId) => {
  try {
    const response = await api.get(`/orders/${orderId}/available-transitions/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching available transitions for order ${orderId}:`, error);
    throw error;
  }
};


const startPreparingOrder = async (orderId, notes = '') => {
  try {
    const response = await api.post(`/orders/${orderId}/start-preparing/`, { notes });
    return response.data;
  } catch (error) {
    console.error(`Error starting preparation for order ${orderId}:`, error);
    throw error;
  }
};

const markOrderReady = async (orderId, notes = '') => {
  try {
    const response = await api.post(`/orders/${orderId}/mark-ready/`, { notes });
    return response.data;
  } catch (error) {
    console.error(`Error marking order ${orderId} as ready:`, error);
    throw error;
  }
};

const completeOrder = async (orderId, notes = '') => {
  try {
    const response = await api.post(`/orders/${orderId}/complete/`, { notes });
    return response.data;
  } catch (error) {
    console.error(`Error completing order ${orderId}:`, error);
    throw error;
  }
};

const cancelOrderFSM = async (orderId, reason = '', notes = '') => {
  try {
    const response = await api.post(`/orders/${orderId}/cancel/`, { reason, notes });
    return response.data;
  } catch (error) {
    console.error(`Error cancelling order ${orderId}:`, error);
    throw error;
  }
};

const orderService = {
  // Public endpoints
  createPublicOrder,
  getPublicOrderStatus,
  getPublicOrderByNumber,
  trackOrder,
  getCustomerOrders,

  // Admin endpoints
  getOrders,
  getOrder,
  updateOrder,
  updateOrderStatus,
  cancelOrder,
  markOrderAsPaid,
  getAvailableDiscounts,

  getOrderItems,
  getOrderItem,
  updateOrderItem,
  createOrderItem,
  deleteOrderItem,

  getOrderStatusHistory,

  // Statistics endpoints
  getOrderStatistics,
  getOrdersByStatus,
  getOrdersByStatusPaginated,
  getOrdersByDate,
  getPopularItems,
  getPeakHours,
  getStatusBreakdown,

  // FSM Transition endpoints
  getAvailableTransitions,
  startPreparingOrder,
  markOrderReady,
  completeOrder,
  cancelOrderFSM
};

export default orderService;
